import React from "react";
import { cn } from "../../utils";
import { Popover, PopoverTrigger, PopoverContent } from "../popover/Popover";

export interface TransparencyAnnotation {
  text: string;
  tier: "semantic_metric" | "data_query" | "ai_computed";
}

const TIER_CONFIG = {
  semantic_metric: {
    symbol: "\u25CF",
    color: "al-text-blue-500",
    markerBg: "al-bg-blue-50 dark:al-bg-blue-900/40",
    border: "al-border-blue-200 dark:al-border-blue-800",
  },
  data_query: {
    symbol: "\u25C6",
    color: "al-text-teal-600",
    markerBg: "al-bg-teal-100 dark:al-bg-teal-900/40",
    border: "al-border-teal-200 dark:al-border-teal-800",
  },
  ai_computed: {
    symbol: "\u25CF",
    color: "al-text-amber-500",
    markerBg: "al-bg-amber-50 dark:al-bg-amber-900/40",
    border: "al-border-amber-200 dark:al-border-amber-800",
  },
} as const;

export const TransparencyMarker: React.FC<{
  annotation: TransparencyAnnotation;
  /** Rendered markdown content shown in the popover. */
  popoverContent: React.ReactNode;
  /** When provided, this element becomes the clickable trigger instead of a badge. */
  trigger?: React.ReactNode;
}> = ({ annotation, popoverContent, trigger }) => {
  const config = TIER_CONFIG[annotation.tier] ?? TIER_CONFIG.data_query;

  return (
    <Popover>
      <PopoverTrigger asChild>
        {trigger ? (
          <span
            className={cn(
              "al-cursor-pointer al-select-none",
              "al-underline al-decoration-dotted al-underline-offset-2",
              config.color,
              "hover:al-opacity-80 al-transition-opacity"
            )}
            role="button"
            tabIndex={0}
          >
            {trigger}
          </span>
        ) : (
          <sup
            className={cn(
              "al-inline-flex al-items-center",
              "al-rounded al-border al-px-1",
              "al-text-[10px] al-font-medium al-leading-none",
              "al-ml-0.5 al-cursor-pointer al-select-none",
              "hover:al-opacity-80 al-transition-opacity",
              config.color,
              config.markerBg,
              config.border
            )}
            role="button"
            tabIndex={0}
          >
            {annotation.text || config.symbol}
          </sup>
        )}
      </PopoverTrigger>
      <PopoverContent
        align="start"
        side="bottom"
        className="al-w-80 al-p-3 al-overflow-auto al-max-h-72"
      >
        {popoverContent}
      </PopoverContent>
    </Popover>
  );
};
